/**
 * The Factory allows the lookup and construction of objects/results based on
 * a type. A sample usage is:
 * 
 * const f = new Factory( )
 * f.add( 'myType', MyTypeMsg )
 * f.add( 'myOtherType', MyOtherTypeMsg )
 * const msg = f.create( { type : 'myOtherType', value : 5 } )
 * // msg is equivalent to new MyOtherMsg( { type : 'myOtherType', value : 5 } )
 *
 * 
 * 
 * Strategies when create( obj, arg1, argN ) is called. Below, clzz equals get( obj )
 *   ALL (default): 
 *     new clzz( obj, arg1, argN ) // clzz is a class
 *     clzz( obj, arg1, argN )     // clzz is a function
 *   NONE: 
 *     new clzz( )
 *     clzz( )
 *   ARGS: 
 *     new clzz( arg1, argN )
 *     clzz( arg1, argN )
 *   MERGE: 
 *     const alt1 = { model : obj, ...arg1 }
 *     new clzz( alt1, argN )
 *     clzz( alt1, argN )
 *   LOOKUP: 
 *     clzz
 *   <function>:
 *     return strategy( this, clzz, obj, arg1, argN )
 *
 * ALL is equivalent to ARGS is create( model, model, arg1, argN ) is called
 **/
class Factory {
  static ALL = allStrategy;
  static NONE = noneStrategy;
  static ARGS = argsStrategy;
  static MERGE = mergeStrategy();
  static LOOKUP = lookupStrategy;

  /**
   * Create a factory object.
   * @param {object|function} options the options for the factory. If this is a function,
   * it is the strategy.
   * @param {string} name the name of the factory
   * @param {string} typeKey the property to look at when determining the type of an object. Defaults to 'type'
   * @param {object} [registry] a type to obj map. Using this is the same as calling add for each key/value pair.
   * @param {any} defaultObject If a lookup results in null/undefined, this object will be used 
   * @param {function} strategy a function( factory, clzz, typeOrClass, ...args ) invoked to 
   * create the returned object. (Defaults to Factory.ALL)
   **/
  constructor(options = {}) {
    const opts = typeof options === 'function' ? {
      strategy: options
    } : {
      ...options
    };
    this.options = opts;
    this.name = opts.name || 'Factory';
    this.typeKey = opts.typeKey || 'type';
    this.strategy = opts.strategy || Factory.ALL;
    this.defaultObject = opts.defaultObject || null;
    if (typeof this.strategy !== 'function') {
      throw new Error('Invalid strategy');
    }
    this.registry = opts.registry ? {
      ...opts.registry
    } : {};
  }

  /**
   * Registers an object to support the type. If there
   * is already an object registered for the type, this
   * will throw an exception unless overwrite is set to true
   * @param {string} type the type to register
   * @param {any} obj the thing to register for the type
   **/
  add(type, obj, overwrite = false) {
    if (this.registry[type] && !overwrite) {
      throw new Error(`${this.name}: ${type} is already registered`);
    }
    if (type == null) {
      throw Error('type is not valid');
    }
    if (obj == null) {
      delete this.registry[type];
    } else {
      this.registry[type] = obj;
    }
  }

  /**
   * Removes a registered type.
   * @param {string} type the type to remove
   **/
  remove(type) {
    delete this.registry[type];
  }

  /**
   * If typeOrObj is an object, this will extract the type from 
   * the object usin the typeKey field. Otherwise, typeOrObject
   * will be returned
   * @param {any} typeOrObj the type or an object containing the type
   * @return the type
   **/
  getType(typeOrObj) {
    let type = typeOrObj;
    if (typeOrObj && typeof typeOrObj === 'object') {
      type = typeOrObj[this.typeKey];
    }
    return type === undefined ? null : type;
  }

  /**
   * Return true if an object is registered to suppor the type.
   * @return {boolean} true if there is a registered object
   **/
  has(typeOrObject, returnDefault = true) {
    return this.get(typeOrObject, returnDefault) != null;
  }

  /**
   * Returns the function/class registered to manager
   * the object of the specified type. 
   * @param {object|string} type If this is a string, it is
   * used as the type to lookup. If it is an object, the typeKey
   * field of the object is used to do the lookup.
   * @return {any} the thing registered at that that location
   **/
  get(typeOrObj, returnDefault = true) {
    const type = this.getType(typeOrObj);
    return this.registry[type] || (returnDefault ? this.defaultObject : null);
  }

  /**
   * Returns an object containing the mapping between
   * the types and the things registered to suppor that
   * type.
   * @return {object} the mapping
   **/
  getAll() {
    return {
      ...this.registry
    };
  }

  /**
   * Creates the thing used to support the typeOfObj.
   * @param {object|string} typeOrObj usually the object we're creating
   * a handler for, but in some strategies, this might just be the type
   * @param {array} args the rest of the params
   * @return {any} the constructed item
   **/
  create(typeOrObj, ...args) {
    let clzz = this.get(typeOrObj);
    if (clzz && this.options.creationKey) {
      clzz = clzz[this.options.creationKey];
    }
    if (clzz) {
      return this.strategy(this, clzz, typeOrObj, ...args);
    }
    return null;
  }
}

/** 
 * A strategy where the args passed into the constructor/function
 * are ( typeOrObject, arg1, arg2, ...etc... )
 * @param {Factory} factory the invoking factory
 * @param {Class|function} clzz the registered 
 * @param {object|string} typeOrObject the object used to lookup the clzz
 * @param {array} ...args the rest of the args
 * @return the new class, the result of the function, or clzz if
 * clzz is not a class or function.
 **/
function allStrategy(factory, clzz, typeOrObject, ...args) {
  if (isClass(clzz)) {
    return new clzz(typeOrObject, ...args); // eslint-disable-line
  }
  return typeof clzz === 'function' ? clzz(typeOrObject, ...args) : clzz;
}

/** 
 * A strategy where no args passed into the constructor/function
 * @param {Factory} factory the invoking factory
 * @param {Class|function} clzz the registered 
 * @param {object|string} typeOrObject the object used to lookup the clzz
 * @param {array} ...args the rest of the args
 * @return the new class, the result of the function, or clzz if
 * clzz is not a class or function.
 **/
function noneStrategy(factory, clzz, typeOrObject, ...args) {
  if (isClass(clzz)) {
    return new clzz(); // eslint-disable-line
  }
  return typeof clzz === 'function' ? clzz() : clzz;
}

/** 
 * A strategy where the args passed into the constructor/function
 * are ( arg1, arg2, ...etc... )
 * @param {Factory} factory the invoking factory
 * @param {Class|function} clzz the registered 
 * @param {object|string} typeOrObject the object used to lookup the clzz
 * @param {array} ...args the rest of the args
 * @return the new class, the result of the function, or clzz if
 * clzz is not a class or function.
 **/
function argsStrategy(factory, clzz, typeOrObject, ...args) {
  if (isClass(clzz)) {
    return new clzz(...args); // eslint-disable-line
  }
  return typeof clzz === 'function' ? clzz(...args) : clzz;
}

/** 
 * A strategy where clzz is returned
 * @param {Factory} factory the invoking factory
 * @param {Class|function} clzz the registered 
 * @param {object|string} typeOrObject the object used to lookup the clzz
 * @param {array} ...args the rest of the args
 * @return the new class, the result of the function, or clzz if
 * clzz is not a class or function.
 **/
function lookupStrategy(factory, clzz, typeOrObject, ...args) {
  return clzz;
}

/** 
 * Creates a strategy where the args passed into the constructor/function
 * are ( MOD, arg2, ...etc... ), where MOD is { [key] : typeOrObject, ...args[0] }
 * @param {string} key the the merge key
 * @return the new merge strategy
 **/
function mergeStrategy(key = 'model') {
  return (factory, clzz, typeOrObject, ...args) => {
    const opts = [...args];
    opts[0] = {
      [key]: typeOrObject,
      ...(opts[0] || {})
    };
    if (isClass(clzz)) {
      return new clzz(...opts); // eslint-disable-line
    }
    return typeof clzz === 'function' ? clzz(...opts) : clzz;
  };
}

/** 
 * Creates a strategy for createing react elements. The clzz object given to the
 * strategy is assumed to be a React.Component or React pure component/function.
 * the typeOrObejct will be merge into the props at the specified propKey.
 * @param {React} React the React library
 * @param {string|false} propKey if this is a string, the typeOrObject will be
 * merged into the props at that key. Otherwise, it will not be supplied
 * @return the react element
 **/
function reactStrategy(React, propKey = 'model') {
  return (factory, clzz, typeOrObject, props /* ignore rest */) => {
    return typeof propKey === 'string' ? React.createElement(clzz, {
      ...props,
      [propKey]: typeOrObject
    }) : React.createElement(clzz, props);
  };
}
function isClass(func) {
  return typeof func === 'function' && /^class\s/.test(Function.prototype.toString.call(func));
}
Factory.reactStrategy = reactStrategy;
Factory.mergeStrategy = mergeStrategy;
export default Factory;